import React, { createContext, useContext, useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Dumbbell, User, CheckSquare, QrCode, LogOut, Moon, Sun, Clock, Activity, Home, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getMyMemberRecord, getMyGym } from '../../lib/memberDb';
import { getActiveGymNotices } from '../../lib/gymNotices';

import MyHome from './MyHome';
import MyInfo from './MyInfo';
import MyMembership from './MyMembership';
import MyAttendance from './MyAttendance';
import MyPT from './MyPT';
import MyBody from './MyBody';
import MyQR from './MyQR';
import MemberHelp from './MemberHelp';

// 회원 포털 전용 Context
export const MemberPortalContext = createContext(null);
export const useMemberPortal = () => useContext(MemberPortalContext);

const navItems = [
  { path: '/my/home', icon: Home, label: '홈' },
  { path: '/my/qr', icon: QrCode, label: 'QR' },
  { path: '/my/attendance', icon: CheckSquare, label: '출석' },
  { path: '/my/pt', icon: Dumbbell, label: 'PT' },
  { path: '/my/body', icon: Activity, label: '신체' },
  { path: '/my/info', icon: User, label: '내 정보' },
];

export default function MemberPortal() {
  const { signOut } = useAuth();
  const { darkMode, setDarkMode } = useApp();
  const [memberRecord, setMemberRecord] = useState(null);
  const [gym, setGym] = useState(null);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const rec = await getMyMemberRecord();
        setMemberRecord(rec);
        if (rec?.gymId) {
          const [gymData, noticeData] = await Promise.all([
            getMyGym(rec.gymId),
            getActiveGymNotices(rec.gymId),
          ]);
          setGym(gymData);
          setNotices(noticeData);
        }
      } catch (err) {
        console.error('[MemberPortal] load error:', err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // DB 오류
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">회원 정보를 불러올 수 없습니다</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            일시적인 오류가 발생했습니다.<br />
            헬스장에 문의해주세요.
          </p>
          <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto">
            <LogOut className="w-4 h-4" /> 로그아웃
          </button>
        </div>
      </div>
    );
  }

  // 가입 승인 대기 중
  if (memberRecord?.status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">가입 승인 대기 중</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            관리자가 가입 신청을 검토 중입니다.<br />
            승인 완료 후 이용하실 수 있습니다.
          </p>
          <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto">
            <LogOut className="w-4 h-4" /> 로그아웃
          </button>
        </div>
      </div>
    );
  }

  // 계정 미연동 상태
  if (!memberRecord) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">계정 연동 대기 중</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            관리자가 회원 정보에 이 전화번호를 등록해야 합니다.<br />
            담당 직원에게 문의해주세요.
          </p>
          <button onClick={signOut} className="btn-secondary flex items-center gap-2 mx-auto">
            <LogOut className="w-4 h-4" /> 로그아웃
          </button>
        </div>
      </div>
    );
  }

  return (
    <MemberPortalContext.Provider value={{ memberRecord, gym }}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        {/* 공지 배너 */}
        {notices.map(n => (
          <div key={n.id} className="bg-blue-600 text-white px-4 py-2 text-sm flex-shrink-0">
            <span className="font-medium">[공지] </span>{n.title}
            {n.content && <span className="text-blue-100 ml-1">— {n.content}</span>}
          </div>
        ))}
        {/* 헤더 */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Dumbbell className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-gray-900 dark:text-white truncate">{memberRecord.name}님</div>
            {gym?.name && (
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{gym.name}</div>
            )}
          </div>
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            onClick={signOut}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        {/* 페이지 */}
        <main className="flex-1 overflow-y-auto pb-20">
          <Routes>
            <Route path="/my/home" element={<MyHome />} />
            <Route path="/my/membership" element={<MyMembership />} />
            <Route path="/my/attendance" element={<MyAttendance />} />
            <Route path="/my/pt" element={<MyPT />} />
            <Route path="/my/body" element={<MyBody />} />
            <Route path="/my/qr" element={<MyQR />} />
            <Route path="/my/info" element={<MyInfo />} />
            <Route path="/my/help" element={<MemberHelp />} />
            <Route path="*" element={<Navigate to="/my/home" replace />} />
          </Routes>
        </main>

        {/* 하단 탭 */}
        <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex">
          {navItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-colors ${
                  isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </MemberPortalContext.Provider>
  );
}
